import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MUITooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';

import icSwap from 'core/assets/svg/icSwap.svg';
import icReset from 'core/assets/svg/ic-reset.svg';
import { useActionStyles } from '../versions.styles';

/**
 * Per-row Compare + Revert actions for the version-history modal, rendered as icon buttons with
 * hover tooltips (GH #29604, design update). Disabled actions stay visible but grayed in place, with
 * a tooltip explaining why — the tooltip wraps a span so hover still fires on the disabled element.
 *
 * - Compare: opens the ClarionDoor version diff in a new tab. Disabled when there's no prior version
 *   to compare against (v001) or for farm policies.
 * - Revert: opens the revert modal. Disabled on the current version (nothing to revert to).
 */
const VersionActions = ({
  showCompare,
  compareHref,
  canCompare,
  compareDisabledTooltip,
  showRevert,
  canRevert,
  onRevert
}) => {
  const classes = useActionStyles();

  return (
    <div className={classes.actions}>
      {showCompare &&
        (canCompare ? (
          <MUITooltip title="Compare to previous version" placement="top" classes={{ tooltip: classes.tooltip }}>
            <a
              href={compareHref}
              target="_blank"
              rel="noreferrer noopener"
              className={classes.iconButton}
              aria-label="Compare to previous version"
            >
              <img src={icSwap} alt="" className={classes.icon} />
            </a>
          </MUITooltip>
        ) : (
          <MUITooltip title={compareDisabledTooltip} placement="top" classes={{ tooltip: classes.tooltip }}>
            <span className={classes.iconButton}>
              <img src={icSwap} alt="" className={classNames(classes.icon, classes.iconDisabled)} />
            </span>
          </MUITooltip>
        ))}

      {showRevert &&
        (canRevert ? (
          <MUITooltip title="Revert to this policy version" placement="top" classes={{ tooltip: classes.tooltip }}>
            <IconButton onClick={onRevert} className={classes.iconButton} aria-label="Revert to this policy version">
              <img src={icReset} alt="" className={classes.icon} />
            </IconButton>
          </MUITooltip>
        ) : (
          <MUITooltip title="This is the current version" placement="top" classes={{ tooltip: classes.tooltip }}>
            <span className={classes.iconButton}>
              <img src={icReset} alt="" className={classNames(classes.icon, classes.iconDisabled)} />
            </span>
          </MUITooltip>
        ))}
    </div>
  );
};

VersionActions.propTypes = {
  showCompare: PropTypes.bool.isRequired,
  compareHref: PropTypes.string,
  canCompare: PropTypes.bool.isRequired,
  compareDisabledTooltip: PropTypes.string.isRequired,
  showRevert: PropTypes.bool.isRequired,
  canRevert: PropTypes.bool.isRequired,
  onRevert: PropTypes.func
};

export default VersionActions;
